/**
 * PhotoReleaseQuestion — the club's photo / media question on a camp, clinic
 * or event registration form (Dan and Chris, 2026-10-07: "why not have that a
 * part of registration… especially for a camp").
 *
 * It is the SAME waiver member sign-up asks as yes / no (the one the club
 * marked "This is the photo and media release"), and the answer is recorded
 * by the same code on the server. Every registration form uses this one hook
 * and this one component:
 *
 *   const photo = usePhotoReleaseQuestion("member" | "public");
 *   …
 *   <PhotoReleaseQuestion photo={photo} />
 *   …
 *   await register({ …formData, ...photo.payload });
 *
 * Rules it keeps:
 *   - It is never required. No answer is fine and nothing blocks the Register
 *     or Pay button.
 *   - Nothing is shown when the club has no photo release, when a signed-in
 *     family has already answered it, or when the question could not be
 *     loaded. A registration form must never break because of it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { CameraOff } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { getLightBorder, getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import {
  getMyPhotoReleaseQuestion,
  getPublicPhotoReleaseQuestion,
  PhotoReleaseQuestionData,
} from "../../api/waiverRequestsAPI";
import { safeGetItem } from "@mediatechgroup/app-version";

export type PhotoAnswer = "yes" | "no" | null;

export interface PhotoReleaseState {
  /** null = there is nothing to ask. */
  question: PhotoReleaseQuestionData | null;
  answer: PhotoAnswer;
  setAnswer: (next: PhotoAnswer) => void;
  /** Spread into the registration request. Empty when not answered. */
  payload: { photo_release?: { waiver_id: string; answer: "yes" | "no" } };
}

/**
 * @param mode "member" for a signed-in parent (asks only if their family has
 *   not answered), "public" for a public page (the club's wording only).
 * @param enabled pass false to wait (a modal that is not open yet).
 */
export function usePhotoReleaseQuestion(mode: "member" | "public", enabled = true): PhotoReleaseState {
  const [question, setQuestion] = useState<PhotoReleaseQuestionData | null>(null);
  const [answer, setAnswer] = useState<PhotoAnswer>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const read =
      mode === "member"
        ? getMyPhotoReleaseQuestion(safeGetItem("authToken") || "")
        : getPublicPhotoReleaseQuestion();
    read
      .then((found) => {
        if (!cancelled) setQuestion(found);
      })
      .catch(() => {
        // Optional by design: a form that cannot load the question does not ask it.
        if (!cancelled) setQuestion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, enabled]);

  return useMemo(
    () => ({
      question,
      answer,
      setAnswer,
      payload: question && answer ? { photo_release: { waiver_id: question.waiver_id, answer } } : {},
    }),
    [question, answer]
  );
}

const PhotoReleaseQuestion: React.FC<{ photo: PhotoReleaseState; className?: string }> = ({ photo, className = "" }) => {
  const { isDarkMode } = useTheme();
  const { question, answer, setAnswer } = photo;
  if (!question) return null;

  const primary = getPrimaryText(isDarkMode);
  const muted = getSecondaryText(isDarkMode);
  const name = `photo-release-${question.waiver_id}`;

  return (
    <fieldset
      data-testid="photo-release-question"
      className={`rounded-lg border ${getLightBorder(isDarkMode)} p-4 space-y-3 ${className}`}
    >
      <legend className={`px-1 text-sm font-semibold ${primary}`}>{question.name}</legend>
      <div className={`max-h-48 overflow-y-auto space-y-2 text-sm ${muted}`}>
        {question.description && <p>{question.description}</p>}
        {question.blocks.map((block) =>
          block.type === "heading" ? (
            <p key={block.id} className={`font-semibold ${primary}`}>
              {block.content}
            </p>
          ) : block.type === "text" ? (
            <p key={block.id} className="whitespace-pre-wrap">
              {block.content}
            </p>
          ) : null
        )}
      </div>
      <div className="space-y-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name={name}
            className="mt-0.5 h-4 w-4 accent-[var(--cc-action)]"
            checked={answer === "yes"}
            onChange={() => setAnswer("yes")}
          />
          <span className={`text-sm ${primary}`}>Yes, photos and video are OK.</span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name={name}
            className="mt-0.5 h-4 w-4 accent-[var(--cc-action)]"
            checked={answer === "no"}
            onChange={() => setAnswer("no")}
          />
          <span className={`text-sm ${primary}`}>No, please do not take or share photos or video.</span>
        </label>
      </div>
      {answer === "no" && (
        <p className={`flex items-start gap-2 text-sm ${muted}`}>
          <CameraOff className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>Coaches will see a "No photos" mark beside the name on the roster.</span>
        </p>
      )}
      <p className={`text-xs ${muted}`}>
        Optional. You can finish registering without answering
        {answer ? (
          <>
            {" "}
            <button
              type="button"
              className="underline text-[var(--cc-link)]"
              onClick={() => setAnswer(null)}
            >
              (clear my answer)
            </button>
          </>
        ) : null}
        .
      </p>
    </fieldset>
  );
};

export default PhotoReleaseQuestion;
